import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/buttons/Button/Button';
import { FieldRecommendation } from '~/components/forms/FieldRecommendation/FieldRecommendation';
import { loadCategoryOptions, loadTypeOptions } from '~/features/vault/profile/api';
import type { OptionNumType } from '~/types';
import {
  MODAL_BACKDROP_CLASS,
  MODAL_CLOSE_BTN_CLASS,
  MODAL_INPUT_CLASS,
  MODAL_LABEL_CLASS,
  MODAL_PANEL_WIDE_CLASS,
  MODAL_SELECT_CLASS,
} from './modalStyles';
import {
  findAddFormError,
  type AddConflict,
  type AddNpiLookup,
  type AddProviderForm,
} from './providerAssociation.machine';

const TAGS = CT.employer.providerAssociations.addModal;

interface AddManagedProviderModalProps {
  open: boolean;
  submitting: boolean;
  form: AddProviderForm;
  validationError: string;
  npiError: string;
  // Bumped by the machine on every rejected submit; moves focus to the first invalid field.
  submitFailures: number;
  npiLookup: AddNpiLookup | null;
  conflict: AddConflict | null;
  onClose: () => void;
  onFieldChange: (field: keyof AddProviderForm, value: string | number | boolean | null) => void;
  onNpiBlur: () => void;
  onSubmit: () => void;
  onSwitchToInvite: () => void;
}

const FIELD_IDS: Partial<Record<keyof AddProviderForm, string>> = {
  firstName: 'add-provider-first-name',
  lastName: 'add-provider-last-name',
  providerCategoryId: 'add-provider-category',
  providerTypeId: 'add-provider-type',
  npiNum: 'add-provider-npi',
  email: 'add-provider-email',
};
const ERROR_ID = 'add-provider-error';
const NPI_ERROR_ID = 'add-provider-npi-error';
const EMAIL_HELPER_ID = 'add-provider-email-helper';

// [MCV-1960] Same marker FormLabel draws for a required field. It is visual only: the field
// carries aria-required, so its accessible name stays the bare label.
const RequiredMark = () => (
  <span className="pl-px text-content-primary" aria-hidden="true">
    *
  </span>
);

type Notice = { tag: string; text: string; offerInvite: boolean };

// What to tell the employer about the NPI / email they typed, and whether a
// one-click handoff to the Invite modal makes sense. Server conflicts win over
// the (advisory) blur lookup.
const noticeFor = (lookup: AddNpiLookup | null, conflict: AddConflict | null): Notice | null => {
  if (conflict) {
    if (conflict.code === 'PENDING_REQUEST_EXISTS') {
      return {
        tag: TAGS.conflictMsg,
        text: 'An invitation to this email is already pending for a provider you added. Check the Invitations tab to resend it.',
        offerInvite: false,
      };
    }
    if (conflict.code === 'NPI_IN_USE' && conflict.existingProviderId != null) {
      return {
        tag: TAGS.conflictMsg,
        text: 'You already added a provider with this NPI.',
        offerInvite: false,
      };
    }
    if (conflict.code === 'NPI_IN_USE') {
      return {
        tag: TAGS.npiHandoffMsg,
        text: 'This NPI already belongs to a provider. Send an invitation instead?',
        offerInvite: true,
      };
    }
    return {
      tag: TAGS.conflictMsg,
      text: 'A provider with this email already exists. Send an invitation instead?',
      offerInvite: true,
    };
  }
  if (lookup?.match === 'single') {
    if (lookup.managedByYou) {
      return {
        tag: TAGS.npiManagedMsg,
        text: 'You already added a provider with this NPI.',
        offerInvite: false,
      };
    }
    return {
      tag: TAGS.npiHandoffMsg,
      text: 'This NPI already belongs to a provider. Send an invitation instead?',
      offerInvite: true,
    };
  }
  if (lookup?.match === 'ambiguous') {
    return {
      tag: TAGS.npiAmbiguousMsg,
      text: 'Several providers use this NPI; invite by email.',
      offerInvite: true,
    };
  }
  return null;
};

export const AddManagedProviderModal = ({
  open,
  submitting,
  form,
  validationError,
  npiError,
  submitFailures,
  npiLookup,
  conflict,
  onClose,
  onFieldChange,
  onNpiBlur,
  onSubmit,
  onSwitchToInvite,
}: AddManagedProviderModalProps) => {
  const [categories, setCategories] = useState<OptionNumType[]>([]);
  // Type options remember the category they were loaded for, so options from a previous
  // category are never offered (or submitted) under a new one while the new ones load.
  const [typeOptions, setTypeOptions] = useState<{
    categoryId: number | null;
    options: OptionNumType[];
  }>({ categoryId: null, options: [] });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadCategoryOptions()
      .then((options) => !cancelled && setCategories(options))
      .catch(() => !cancelled && setCategories([]));
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const categoryId = form.providerCategoryId;
    if (!open || categoryId == null) return;
    let cancelled = false;
    loadTypeOptions(categoryId)
      .then((options) => !cancelled && setTypeOptions({ categoryId, options }))
      .catch(() => !cancelled && setTypeOptions({ categoryId, options: [] }));
    return () => {
      cancelled = true;
    };
  }, [open, form.providerCategoryId]);

  const typesLoading =
    form.providerCategoryId != null && typeOptions.categoryId !== form.providerCategoryId;
  const types = form.providerCategoryId != null && !typesLoading ? typeOptions.options : [];

  // The field to flag: a server/blur NPI error, else the first field the form check rejects.
  const invalidField: keyof AddProviderForm | null = npiError
    ? 'npiNum'
    : validationError
      ? (findAddFormError(form)?.field ?? null)
      : null;
  const handledFailures = useRef(0);
  useEffect(() => {
    if (submitFailures <= handledFailures.current) return;
    handledFailures.current = submitFailures;
    const id = invalidField ? FIELD_IDS[invalidField] : undefined;
    if (id) document.getElementById(id)?.focus();
  }, [submitFailures, invalidField]);

  const invalidProps = (field: keyof AddProviderForm) =>
    invalidField === field
      ? {
          'aria-invalid': true as const,
          'aria-describedby': field === 'npiNum' ? NPI_ERROR_ID : ERROR_ID,
        }
      : {};

  const notice = noticeFor(npiLookup, conflict);
  const hasEmail = form.email.trim() !== '';
  const idFor = (name: string) => `add-provider-${name}`;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop transition className={MODAL_BACKDROP_CLASS} />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel transition data-cy={TAGS.modal} className={MODAL_PANEL_WIDE_CLASS}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlusIcon className="h-5 w-5 text-content-secondary" />
                <DialogTitle className="text-lg font-semibold text-grey-100">
                  Add Provider
                </DialogTitle>
              </div>
              <button onClick={onClose} className={MODAL_CLOSE_BTN_CLASS} type="button">
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5 text-content-secondary" />
              </button>
            </div>

            <p className="mb-4 text-sm text-content-secondary">
              Create a vault for a provider who has no account yet. You manage it until they sign up
              and claim it.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              noValidate
            >
              <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-3">
                <div>
                  <label htmlFor={idFor('prefix')} className={MODAL_LABEL_CLASS}>
                    Prefix
                  </label>
                  <input
                    id={idFor('prefix')}
                    data-cy={TAGS.prefixInput}
                    type="text"
                    value={form.prefixName}
                    onChange={(e) => onFieldChange('prefixName', e.target.value)}
                    placeholder="Dr."
                    className={MODAL_INPUT_CLASS}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor={idFor('first-name')} className={MODAL_LABEL_CLASS}>
                    First name
                    <RequiredMark />
                  </label>
                  <input
                    id={idFor('first-name')}
                    {...invalidProps('firstName')}
                    aria-required
                    data-cy={TAGS.firstNameInput}
                    type="text"
                    value={form.firstName}
                    onChange={(e) => onFieldChange('firstName', e.target.value)}
                    className={MODAL_INPUT_CLASS}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor={idFor('last-name')} className={MODAL_LABEL_CLASS}>
                    Last name
                    <RequiredMark />
                  </label>
                  <input
                    id={idFor('last-name')}
                    {...invalidProps('lastName')}
                    aria-required
                    data-cy={TAGS.lastNameInput}
                    type="text"
                    value={form.lastName}
                    onChange={(e) => onFieldChange('lastName', e.target.value)}
                    className={MODAL_INPUT_CLASS}
                    disabled={submitting}
                  />
                </div>
              </div>

              <label htmlFor={idFor('email')} className={`${MODAL_LABEL_CLASS} mt-2`}>
                Email (recommended)
              </label>
              <input
                id={idFor('email')}
                {...invalidProps('email')}
                // The helper stays in the description beside a validation error.
                aria-describedby={
                  invalidField === 'email' ? `${EMAIL_HELPER_ID} ${ERROR_ID}` : EMAIL_HELPER_ID
                }
                data-cy={TAGS.emailInput}
                type="email"
                value={form.email}
                onChange={(e) => onFieldChange('email', e.target.value)}
                placeholder="provider@example.com"
                className={MODAL_INPUT_CLASS}
                disabled={submitting}
              />
              <FieldRecommendation
                id={EMAIL_HELPER_ID}
                cyTag={TAGS.emailHelper}
                message="Recommended so the provider can claim this vault when they sign up."
              />

              <label className="mt-2 flex items-center gap-2 text-sm text-content-primary has-enabled:cursor-pointer has-disabled:cursor-not-allowed">
                <input
                  data-cy={TAGS.sendInvitationToggle}
                  type="checkbox"
                  checked={form.sendInvitation && hasEmail}
                  onChange={(e) => onFieldChange('sendInvitation', e.target.checked)}
                  className="enabled:cursor-pointer disabled:cursor-not-allowed"
                  disabled={submitting || !hasEmail}
                />
                Also send invitation now
              </label>

              <div className="mt-2 grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <div>
                  <label htmlFor={idFor('category')} className={MODAL_LABEL_CLASS}>
                    Provider category
                    <RequiredMark />
                  </label>
                  <select
                    id={idFor('category')}
                    {...invalidProps('providerCategoryId')}
                    aria-required
                    data-cy={TAGS.categorySelect}
                    value={form.providerCategoryId ?? ''}
                    onChange={(e) =>
                      onFieldChange(
                        'providerCategoryId',
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    className={MODAL_SELECT_CLASS}
                    disabled={submitting}
                  >
                    <option value="">Select a category</option>
                    {categories.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor={idFor('type')} className={MODAL_LABEL_CLASS}>
                    Provider type
                    <RequiredMark />
                  </label>
                  <select
                    id={idFor('type')}
                    {...invalidProps('providerTypeId')}
                    aria-required
                    data-cy={TAGS.typeSelect}
                    value={form.providerTypeId ?? ''}
                    onChange={(e) =>
                      onFieldChange(
                        'providerTypeId',
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    className={MODAL_SELECT_CLASS}
                    disabled={submitting || form.providerCategoryId == null || typesLoading}
                  >
                    <option value="">{typesLoading ? 'Loading types...' : 'Select a type'}</option>
                    {types.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label htmlFor={idFor('npi')} className={`${MODAL_LABEL_CLASS} mt-2`}>
                NPI (optional)
              </label>
              <input
                id={idFor('npi')}
                {...invalidProps('npiNum')}
                data-cy={TAGS.npiInput}
                type="text"
                inputMode="numeric"
                value={form.npiNum}
                onChange={(e) => onFieldChange('npiNum', e.target.value)}
                onBlur={onNpiBlur}
                placeholder="10-digit NPI"
                className={MODAL_INPUT_CLASS}
                disabled={submitting}
              />
              {npiError && (
                <p
                  id={NPI_ERROR_ID}
                  role="alert"
                  data-cy={TAGS.npiErrorMsg}
                  className="mb-2 text-sm text-error"
                >
                  {npiError}
                </p>
              )}

              {notice && (
                <div className="mt-3 rounded-md bg-warning-subtle p-3 text-sm text-warning-950">
                  <p data-cy={notice.tag}>{notice.text}</p>
                  {notice.offerInvite && (
                    <div className="mt-2">
                      <Button
                        cyTag={TAGS.handoffConfirmBtn}
                        type="button"
                        colorVariant="secondary"
                        onClick={onSwitchToInvite}
                        disabled={submitting}
                      >
                        Send an invitation instead
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {validationError && validationError !== npiError && (
                <p
                  id={ERROR_ID}
                  role="alert"
                  data-cy={TAGS.errorMsg}
                  className="mt-3 text-sm text-error"
                >
                  {validationError}
                </p>
              )}

              <div className="mt-4 flex justify-end gap-3">
                <Button
                  cyTag={TAGS.cancelBtn}
                  type="button"
                  colorVariant="secondary"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  cyTag={TAGS.submitBtn}
                  type="submit"
                  colorVariant="primary"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Provider'}
                </Button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
